import { showErrorToast } from '@bpinternal/ui-kit-next'
import { QueryClientProvider } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useCallback, useEffect, useState } from 'react'
import { useFlagsStatus } from '@unleash/proxy-client-react'
import { UserSegmentationSurvey } from '~/features/marketing'
import { queryClient } from '~/providers/ReactQuery'
import { userPreferencesQueryOptions, workspacePreferencesQueryOptions } from '~/queries'
import { setAccountPreference } from '~/services/preferences/userPreferences'
import { setWorkspacePreference } from '~/services/preferences/workspacePreferences'
import { useFeature } from '~/hooks/useFeature'

export const Route = createFileRoute('/workspaces/$workspaceId/onboarding')({
  component: OnboardingComponent,
})

function OnboardingComponent() {
  const { workspaceId } = Route.useParams()
  const navigate = useNavigate()
  const [isDone, setIsDone] = useState(false)
  const isOnboardingSurveyEnabled = useFeature('dashboard-onboarding-survey')
  const { flagsReady } = useFlagsStatus()

  const handleDone = useCallback(() => {
    void (async () => {
      try {
        const accountPreferencesQuery = userPreferencesQueryOptions({ path: 'requireOnboarding' })
        const workspacePreferencesQuery = workspacePreferencesQueryOptions(workspaceId, {
          path: 'onBoardingCompleted',
        })

        await Promise.all([
          setAccountPreference({ path: 'requireOnboarding', value: false }),
          setWorkspacePreference({ path: 'onBoardingCompleted', value: true }, workspaceId),
        ])

        queryClient.setQueryData(accountPreferencesQuery.queryKey, false)
        queryClient.setQueryData(workspacePreferencesQuery.queryKey, true)
        setIsDone(true)
        navigate({ to: '/workspaces/$workspaceId/create-bot', params: { workspaceId } })
      } catch (error) {
        showErrorToast(error instanceof Error ? error.message : 'Failed to complete onboarding')
      }
    })()
  }, [navigate, workspaceId])

  useEffect(() => {
    if (flagsReady && !isOnboardingSurveyEnabled) {
      const timeoutId = window.setTimeout(handleDone, 0)

      return () => {
        window.clearTimeout(timeoutId)
      }
    }
  }, [flagsReady, handleDone, isOnboardingSurveyEnabled])

  if (isDone) {
    return null
  }

  return (
    <QueryClientProvider client={queryClient}>
      <UserSegmentationSurvey workspaceId={workspaceId} onDone={handleDone} />
    </QueryClientProvider>
  )
}
